import React, { useEffect, useState } from 'react';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { C, EASE, FONT_DISPLAY, SPRING } from './theme';
import { Icon, IconName, initials } from './ui';
import type { Progress } from './useSchoolInsights';

export type TabKey = 'overview' | 'classes' | 'subjects' | 'teachers' | 'worksheets' | 'students';

/** One sidebar / tab-bar entry. A new `group` starts a captioned section in the sidebar. */
export interface NavItem<K extends string = string> { key: K; label: string; icon: IconName; group?: string }

export const TABS: NavItem<TabKey>[] = [
  { key: 'overview', label: 'Overview', icon: 'grid', group: 'Insights' },
  { key: 'classes', label: 'Classes', icon: 'layers', group: 'Insights' },
  { key: 'subjects', label: 'Subjects', icon: 'book', group: 'Insights' },
  { key: 'teachers', label: 'Teachers', icon: 'teacher', group: 'Insights' },
  { key: 'worksheets', label: 'Worksheets', icon: 'sheet', group: 'Insights' },
  { key: 'students', label: 'Students', icon: 'users', group: 'Records' },
];

/* ─── Parallax backdrop: soft orbs drifting at different depths ── */
export const Backdrop: React.FC = () => {
  const { scrollY } = useScroll();
  const slow = useTransform(scrollY, (v) => v * -0.08);
  const mid = useTransform(scrollY, (v) => v * -0.22);
  const fast = useTransform(scrollY, (v) => v * -0.4);
  const ys = [useSpring(slow, { stiffness: 80, damping: 20 }), useSpring(mid, { stiffness: 80, damping: 20 }), useSpring(fast, { stiffness: 80, damping: 20 })];
  const orbs = [
    { y: ys[0], style: { width: 720, height: 720, left: -220, top: -260, background: 'rgba(10,124,255,0.28)' }, drift: [0, 40, 0] },
    { y: ys[1], style: { width: 600, height: 600, right: -160, top: 120, background: 'rgba(175,82,222,0.22)' }, drift: [0, -50, 0] },
    { y: ys[2], style: { width: 540, height: 540, left: '30%', top: 700, background: 'rgba(20,184,198,0.20)' }, drift: [0, 30, 0] },
    { y: ys[1], style: { width: 460, height: 460, right: '12%', top: 1400, background: 'rgba(255,45,120,0.12)' }, drift: [0, -30, 0] },
  ];
  return (
    <div className="sx-bg" aria-hidden>
      {orbs.map((o, i) => (
        <motion.div key={i} style={{ y: o.y, position: 'absolute', inset: 0 }}>
          <motion.div className="sx-orb" style={o.style as React.CSSProperties} animate={{ x: o.drift }} transition={{ duration: 16 + i * 4, repeat: Infinity, ease: 'easeInOut' }} />
        </motion.div>
      ))}
    </div>
  );
};

/* ─── Sidebar (desktop, floating glass) ───────────────────────── */
export function Sidebar<K extends string>({ items, tab, onTab, schoolName, schoolCode, subtitle, userName, onSignOut, label = 'School dashboard navigation' }: {
  items: NavItem<K>[]; tab: K; onTab: (t: K) => void; schoolName: string; schoolCode: string; subtitle?: string; userName: string; onSignOut: () => void; label?: string;
}) {
  return (
  <motion.aside className="sx-side" aria-label={label} initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.6, ease: EASE }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 8px 18px' }}>
      <span style={{ width: 38, height: 38, borderRadius: 11, background: '#fff', display: 'grid', placeItems: 'center', boxShadow: '0 6px 16px -8px rgba(20,24,60,0.4)', overflow: 'hidden' }}>
        <img src="/smartlearners-logo.png" alt="" style={{ width: 30, height: 30, objectFit: 'contain' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      </span>
      <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 750, fontSize: 16.5, letterSpacing: '-0.02em' }}>
        Smartlearners<span style={{ color: C.blue }}>.ai</span>
      </span>
    </div>
    <div className="sx-tile" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, marginBottom: 6 }}>
      <span style={{ width: 36, height: 36, borderRadius: 11, background: `linear-gradient(140deg, ${C.blue}, ${C.indigo})`, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 12.5, flexShrink: 0 }}>
        {initials(schoolName || schoolCode)}
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{schoolName}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: C.mu }}>{subtitle ?? 'Leadership view'} · {schoolCode}</span>
      </span>
    </div>
    {items.map((t, i) => (
      <React.Fragment key={t.key}>
        {t.group && t.group !== items[i - 1]?.group && <div className="sx-side__caption">{t.group}</div>}
        <button className="sx-side__nav" aria-current={tab === t.key ? 'page' : undefined} onClick={() => onTab(t.key)}>
          {tab === t.key && <motion.span layoutId="sx-nav-active" transition={SPRING} style={{ position: 'absolute', inset: 0, borderRadius: 14, background: `linear-gradient(140deg, ${C.blue}, ${C.indigo})`, boxShadow: '0 10px 24px -12px rgba(10,124,255,0.8)' }} />}
          <Icon name={t.icon} size={18} />
          <span>{t.label}</span>
        </button>
      </React.Fragment>
    ))}
    <div style={{ flex: 1 }} />
    <button onClick={onSignOut} className="sx-side__nav" style={{ marginTop: 8 }} title="Sign out">
      <span style={{ width: 30, height: 30, borderRadius: 99, background: 'rgba(15,20,40,0.07)', display: 'grid', placeItems: 'center', fontSize: 11.5, fontWeight: 700 }}>{initials(userName)}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</span>
        <span style={{ display: 'block', fontSize: 11, color: C.mu }}>Sign out</span>
      </span>
      <Icon name="logout" size={16} color={C.mu} />
    </button>
  </motion.aside>
  );
}

/* ─── Mobile tab bar (floating capsule) ───────────────────────── */
export function TabBar<K extends string>({ items, tab, onTab, label = 'School dashboard sections' }: {
  items: NavItem<K>[]; tab: K; onTab: (t: K) => void; label?: string;
}) {
  return (
  <nav className="sx-tabbar" aria-label={label}>
    {items.map((t) => (
      <button key={t.key} aria-current={tab === t.key ? 'page' : undefined} onClick={() => onTab(t.key)}>
        {tab === t.key && <motion.span layoutId="sx-tab-active" transition={SPRING} style={{ position: 'absolute', inset: 0, borderRadius: 26, background: 'rgba(10,124,255,0.12)' }} />}
        <Icon name={t.icon} size={20} />
        <span>{t.label}</span>
      </button>
    ))}
  </nav>
  );
}

/* ─── Sticky nav bar: compact title fades in as the large title scrolls away ── */
/** "Live data" right after a sync; once results come from the browser cache, how old they are. */
const syncedLabel = (at?: number | null) => {
  const mins = at ? Math.floor((Date.now() - at) / 60000) : 0;
  return mins < 1 ? 'Live data' : `Updated ${mins} min ago`;
};

const IDLE_PROGRESS: Progress = { status: 'idle', done: 0, total: 0 };

export const NavBar: React.FC<{
  title: string; examProgress?: Progress; worksheetProgress?: Progress; onRefresh?: () => void; syncedAt?: number | null; userName: string; onSignOut: () => void;
}> = ({ title, examProgress = IDLE_PROGRESS, worksheetProgress = IDLE_PROGRESS, onRefresh, syncedAt, userName, onSignOut }) => {
  const { scrollY } = useScroll();
  const glass = useTransform(scrollY, [10, 70], [0, 1]);
  const compact = useTransform(scrollY, [50, 110], [0, 1]);
  const compactY = useTransform(scrollY, [50, 110], [8, 0]);
  const busy = examProgress.status === 'loading' || worksheetProgress.status === 'loading';
  const pctDone = examProgress.total ? Math.round((examProgress.done / examProgress.total) * 100) : 0;
  const [menu, setMenu] = useState(false);
  return (
    <div className="sx-navbar">
      <motion.div className="sx-navbar__glass" style={{ opacity: glass }} />
      <motion.button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} title="Back to top"
        style={{ opacity: compact, y: compactY, border: 0, background: 'none', padding: 0, cursor: 'pointer', fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 17, letterSpacing: '-0.02em', color: C.ink, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {title}
      </motion.button>
      <div style={{ flex: 1 }} />
      {busy ? (
        <span className="sx-pill" style={{ background: 'rgba(255,255,255,0.7)', color: C.ink2, boxShadow: `0 0 0 1px ${C.line} inset` }} aria-live="polite">
          <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ display: 'inline-flex' }}><Icon name="refresh" size={13} /></motion.span>
          {examProgress.status === 'loading' && examProgress.total > 0
            ? `Analysing papers · ${pctDone}%`
            : 'Syncing results…'}
        </span>
      ) : (
        <span className="sx-pill" style={{ background: C.greenL, color: C.greenInk }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: C.green }} /> {syncedLabel(syncedAt)}
        </span>
      )}
      {onRefresh && (
        <button className="sx-btn" onClick={onRefresh} disabled={busy} aria-label="Refresh data" style={{ padding: 9, opacity: busy ? 0.5 : 1 }}>
          <Icon name="refresh" size={16} />
        </button>
      )}
      <div className="sx-mobile-only" style={{ position: 'relative' }}>
        <button className="sx-btn" onClick={() => setMenu((m) => !m)} onBlur={() => setTimeout(() => setMenu(false), 150)} aria-label="Account" aria-expanded={menu} style={{ padding: 5 }}>
          <span style={{ width: 28, height: 28, borderRadius: 99, background: `linear-gradient(140deg, ${C.blue}, ${C.indigo})`, color: '#fff', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700 }}>{initials(userName)}</span>
        </button>
        {menu && (
          <div className="sx-card" style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', padding: 6, minWidth: 180, zIndex: 60, background: C.glassStrong }}>
            <button className="sx-side__nav" onMouseDown={(e) => e.preventDefault()} onClick={onSignOut} style={{ color: C.redInk }}>
              <Icon name="logout" size={16} /> Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Large title with a gentle parallax as it scrolls away ──── */
export const LargeTitle: React.FC<{ eyebrow?: string; title: string; sub?: React.ReactNode; right?: React.ReactNode }> = ({ eyebrow, title, sub, right }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 160], [0, 40]);
  const opacity = useTransform(scrollY, [0, 110], [1, 0]);
  const scale = useTransform(scrollY, [0, 160], [1, 0.96]);
  return (
    <motion.header className="sx-largetitle" style={{ y, opacity, scale, transformOrigin: 'left center', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
      <div>
        {eyebrow && <div style={{ fontSize: 13, fontWeight: 600, color: C.mu }}>{eyebrow}</div>}
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
      </div>
      {right}
    </motion.header>
  );
};

/* ─── First-load screen ───────────────────────────────────────── */
const BOOT_STEPS = ['Connecting to your school', 'Loading classes & sections', 'Gathering teacher assessments', 'Measuring student growth'];

export const Boot: React.FC = () => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setStep((s) => Math.min(s + 1, BOOT_STEPS.length - 1)), 650);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="sx-root" style={{ display: 'grid', placeItems: 'center', padding: 24 }}>
      <Backdrop />
      <motion.div className="sx-card" initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.6, ease: EASE }}
        style={{ width: 'min(400px, 100%)', padding: 30, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, background: C.glassStrong }}>
        <motion.span animate={{ scale: [1, 1.06, 1], rotate: [0, -4, 4, 0] }} transition={{ duration: 1.8, repeat: Infinity }}
          style={{ width: 68, height: 68, borderRadius: 20, background: `linear-gradient(140deg, ${C.blue}, ${C.indigo} 60%, ${C.purple})`, display: 'grid', placeItems: 'center', boxShadow: '0 18px 36px -16px rgba(10,124,255,0.8)' }}>
          <Icon name="cap" size={34} color="#fff" />
        </motion.span>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 750, fontSize: 21, letterSpacing: '-0.02em' }}>Preparing your school report</div>
          <div style={{ fontSize: 13, color: C.mu, marginTop: 4 }}>This takes a few seconds.</div>
        </div>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {BOOT_STEPS.map((s, i) => (
            <motion.div key={s} initial={{ opacity: 0, x: -8 }} animate={{ opacity: i <= step ? 1 : 0.35, x: 0 }} transition={{ delay: i * 0.08 }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, fontWeight: 500 }}>
              <span style={{ width: 20, height: 20, borderRadius: 99, display: 'grid', placeItems: 'center', background: i < step ? C.green : 'rgba(15,20,40,0.07)', transition: 'background .3s' }}>
                {i < step ? <Icon name="check" size={12} color="#fff" stroke={3} />
                  : i === step ? <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity }} style={{ width: 7, height: 7, borderRadius: 99, background: C.blue }} /> : null}
              </span>
              {s}
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export const ErrorView: React.FC<{ message: string; onRetry: () => void; onSignOut: () => void }> = ({ message, onRetry, onSignOut }) => (
  <div className="sx-root" style={{ display: 'grid', placeItems: 'center', padding: 24 }}>
    <Backdrop />
    <motion.div className="sx-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ width: 'min(420px, 100%)', padding: 32, textAlign: 'center', background: C.glassStrong }}>
      <span style={{ width: 54, height: 54, borderRadius: 16, background: C.redL, display: 'inline-grid', placeItems: 'center', marginBottom: 14 }}>
        <Icon name="alert" size={26} color={C.red} />
      </span>
      <h2 style={{ margin: '0 0 8px', fontFamily: FONT_DISPLAY, fontSize: 20, letterSpacing: '-0.02em' }}>Couldn't load your school</h2>
      <p style={{ margin: '0 0 22px', fontSize: 14, lineHeight: 1.55, color: C.mu }}>{message}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button className="sx-btn" onClick={onSignOut}>Sign out</button>
        <button className="sx-btn sx-btn--primary" onClick={onRetry}>Try again</button>
      </div>
    </motion.div>
  </div>
);
